import { useEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import SegmentedTabs from "../SegmentedTabs.jsx";
import Select from "../Select.jsx";
import ScopePositionStack from "./ScopePositionStack.jsx";
import ScopeComposition from "./ScopeComposition.jsx";
import AnatomyDiscoverPanel from "./AnatomyDiscoverPanel.jsx";
import RegisterAnatomy from "./RegisterAnatomy.jsx";
import { DIMENSIONS, scopeStepLabel, encodeScopePath } from "../../lib/dimensionDefs.js";
import { useScopeDistinctiveness } from "../../lib/useScopeDistinctiveness.js";
import { useDiscriminatingDescent } from "../../lib/useDiscriminatingDescent.js";
import { DISTINCT_MIN } from "../../lib/dimensionDistinctiveness.js";
import { getChatCreds, isChatConfigured } from "../../lib/chatCreds.js";
import { getRankedModels, defaultModelFor } from "../../lib/modelCatalog.js";
import { runAnatomyDiscovery, scopeNotesFor } from "../../lib/anatomyDiscovery.js";
import { discoverTraceId } from "../../lib/anatomyDiscoveryTrace.js";
import { saveDiscoveryReport } from "../../lib/anatomyDiscoveryStore.js";
import { describeRange } from "../../lib/wikiXray.js";

// Anatomy — the universal "makeup of this scope" lens. A first-class VIEW
// alongside Overview / X-ray / Inventory / Canvases (not a panel bolted under
// the activity surface): switch to it to orient rather than to read the event
// stream.
//
// Two tabs, one orientation surface:
//   • STRUCTURE (default) — deterministic and instant. Two halves on
//     DIFFERENT signals:
//       WHERE YOU ARE — the position stack, which AUTO-CONTINUES down the
//       HIGHEST-VOLUME branch (useDiscriminatingDescent): from your committed
//       levels it greedily drills the dim whose top member captures the most
//       of the scope's prompt volume → that member → repeat, painting the
//       full projected spike chart. Any cell commits.
//       WHO/WHAT DRIVES IT — the composition cross-tab (ScopeComposition),
//       its columns chosen data-driven as the most-DISTINCTIVE breakdowns
//       (JSD vs org, useScopeDistinctiveness), falling back to the stable
//       default when nothing stands out.
//   • DISCOVER — agentic and on-demand (explicit click = explicit spend). A
//     scout samples the scope's own traces and reports what the work IS —
//     themes, endpoint spread, specialist pockets — with the evidence behind
//     every claim (lib/anatomyDiscovery). Structure answers "where is the
//     mass"; Discover answers "what is this place". Shown only when the
//     tenant's chat LLM is configured, like the X-ray's agent-backed tabs.
//
// A third tab, REGISTER, sits last with its label set right and opens only
// when clicked: the organization read as a work register (RegisterAnatomy),
// its groups of people, the areas of work each owns, and the norms and intents
// a register build read out of their sessions. Until register builds are
// served it shows only a reading the viewer opens from a file.
//
// Universal + uniform across EVERY scope and dim INCLUDING clusters (no
// override). The cluster icicle lives on as its own DETAIL lens.

// Module-level so the options object is identity-stable: ScrollArea's options
// effect keys on the object itself, and an inline literal forces a full
// OverlayScrollbars update(true) on every render.
const SCROLL_OPTIONS = Object.freeze({ overflow: { x: "hidden" } });

export default function ScopeAnatomyBody({ nav, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const scopeKey = encodeScopePath(scopePath) || "org";
  const scopeLabel = terminal ? scopeStepLabel(terminal) : "Organization";

  const hero = ctx?.hero || {};
  const range = ctx?.range;
  const onNavigatePath = ctx?.onNavigatePath;

  const [view, setView] = useState("structure");

  // Same availability gate as the X-ray's agent-backed tabs: undefined =
  // still checking (the strip reads busy), any settlement is an answer and
  // the Discover tab stays hidden on a falsy one.
  const [creds, setCreds] = useState(undefined);
  useEffect(() => {
    let on = true;
    getChatCreds()
      .then((c) => { if (on) setCreds(c ?? false); })
      .catch(() => { if (on) setCreds(false); });
    return () => { on = false; };
  }, []);
  const llmReady = Boolean(creds?.provider && creds?.model && isChatConfigured(creds));

  // Model picker for the scout run. Defaults to the tenant's PRIMARY chat
  // model — exactly what an unpicked run resolved to before this control
  // existed — with the provider's catalog one click away to step down to a
  // cheap model or up to a bigger window. Labels carry the context window
  // because that's what you're picking for.
  const defaultModelId = creds ? (creds.model || defaultModelFor(creds.provider)) : "";
  const [modelChoice, setModelChoice] = useState("");
  const scoutModel = modelChoice || defaultModelId;
  const modelOptions = useMemo(() => {
    if (!creds?.provider) return [];
    const ranked = getRankedModels(creds.provider).map(({ model }) => ({
      value: model.id,
      label: model.contextWindow
        ? `${model.name} · ${Math.round(model.contextWindow / 1000)}k`
        : model.name,
    }));
    // A configured model outside the catalog (custom ids) still has to be
    // selectable — surface it verbatim at the top.
    return ranked.some((o) => o.value === scoutModel) || !scoutModel
      ? ranked
      : [{ value: scoutModel, label: scoutModel }, ...ranked];
  }, [creds?.provider, scoutModel]);
  const modelControl = modelOptions.length ? (
    <Select
      value={scoutModel}
      onChange={(v) => setModelChoice(String(v))}
      options={modelOptions}
      ariaLabel="Model for the scout run"
      align="left"
      triggerStyle={{
        // Pin to the LaunchCard CTA's fixed 30px height so the trigger and
        // button line up exactly (inline-flex items-center centers the label).
        height: 30, minHeight: 0,
        fontSize: 11,
        backgroundColor: "var(--bg)",
        backgroundImage: "linear-gradient(var(--control-bg),var(--control-bg))",
      }}
      className="px-[9px]"
    />
  ) : null;

  // Discovery run lifecycle. The RESULT is durable (anatomyDiscoveryStore) —
  // this state is only the run; the panel reads reports from the store. A
  // scope change aborts the run and lands back on Structure: a run started
  // for one scope must not write under another's key, and the deterministic
  // tab is the right first paint for a new scope.
  const [discoverRun, setDiscoverRun] = useState({ status: "idle", progress: null, forKey: null, error: null });
  const discoverAbortRef = useRef(null);
  useEffect(() => {
    setDiscoverRun({ status: "idle", progress: null, forKey: null, error: null });
    // A scoped Register reading follows the scope, so a viewer reading it stays on it; any other tab lands on
    // Structure (a Discover run must not write under another scope's key).
    setView((v) => (v === "register" ? v : "structure"));
    return () => { discoverAbortRef.current?.abort(); };
  }, [scopeKey]);

  async function runDiscover() {
    const key = scopeKey;
    discoverAbortRef.current?.abort();
    const ctrl = new AbortController();
    discoverAbortRef.current = ctrl;
    setDiscoverRun({ status: "running", progress: { queries: 0, note: null }, forKey: key, error: null });
    // The scout gets the scope as text — dim, label, AND id, because its
    // analytics queries must filter on the id while its prose reasons about
    // the label. Same contract as the wiki scout.
    const scopeText = scopePath.length
      ? scopePath.map((s) => `${s.dim}: ${scopeStepLabel(s)} (id: ${s.id})`).join(" › ")
      : "the entire organization";
    const rangeText = describeRange(range);
    const r = await runAnatomyDiscovery({
      scopeText,
      rangeText,
      scopePath,
      range,
      model: scoutModel || null,
      signal: ctrl.signal,
      onProgress: (p) => setDiscoverRun((d) => (d.forKey === key && d.status === "running" ? { ...d, progress: p } : d)),
    });
    if (ctrl.signal.aborted || r.status === "aborted") {
      if (discoverAbortRef.current === ctrl) {
        setDiscoverRun({ status: "idle", progress: null, forKey: null, error: null });
      }
      return;
    }
    if (r.status === "done") {
      // A report that ran but did not persist is a FAILED run — the reader
      // would see the old report under a fresh "profiled" line.
      const persisted = saveDiscoveryReport(null, key, {
        report: r.report,
        trail: r.trail,
        traceBody: r.traceBody || null,
        meta: {
          at: Date.now(), scopeText, rangeText, model: r.model || null,
          scopeNotes: scopeNotesFor(r.scope),
        },
      });
      if (persisted.ok) {
        // A successful save can still carry problems (quota pressure dropped
        // the trace body) — surface them, or "Open as trace" disappears with
        // no explanation.
        setDiscoverRun({
          status: "done", progress: null, forKey: key, error: null,
          notice: persisted.problems.join("; ") || null,
        });
      } else {
        setDiscoverRun({ status: "error", progress: null, forKey: key, error: persisted.problems.join("; ") });
      }
      return;
    }
    setDiscoverRun({ status: "error", progress: null, forKey: key, error: r.error?.message || String(r.error || "discovery failed") });
  }

  // A claim's target is a step APPENDED to the current path — the claim is
  // about a slice of this scope, so the click narrows rather than teleports.
  const onNavigateTarget = onNavigatePath
    ? (target) => onNavigatePath([...scopePath, { dim: target.dim, ids: [String(target.id)], id: String(target.id) }])
    : null;

  // Top-level distinctiveness ranking → data-driven composition columns. Only
  // reorder when there's real signal (top score clears the floor); otherwise
  // null so ScopeComposition keeps its stable per-terminal default (no churn on
  // an org-typical scope).
  const { ranked } = useScopeDistinctiveness(scopePath, range);
  const preferDims = useMemo(
    () => (ranked.length && ranked[0].score >= DISTINCT_MIN ? ranked.map((r) => r.dimKey) : null),
    [ranked],
  );

  // The greedy highest-volume descent → auto-drilled projected rows. Runs
  // at every scope INCLUDING the global root.
  const { descent, loading: descentLoading } = useDiscriminatingDescent(scopePath, range);

  // Distinct eyebrows per cell, like every SegmentedTabs sibling (Store /
  // Lessons, Ask / Monitor): the eyebrow names the mode, the title the tab.
  const tabs = [
    { id: "structure", eyebrow: "Measured", title: "Structure" },
    ...(llmReady ? [{ id: "discover", eyebrow: "Scouted", title: "Discover" }] : []),
    { id: "register", eyebrow: "Read", title: "Register", align: "right" },
  ];

  // One scroll region for both tabs, hero first, the switcher in the page
  // flow directly below it — the SegmentedTabs framing every sibling lens
  // uses (Inventory, memory store), never flush against the LensHost chrome.
  // The page body never scrolls horizontally, enforced twice over:
  //   • grid-cols-[minmax(0,1fr)] — an implicit `auto` track grows to the
  //     widest item's MAX-content, so a wide method flow stretched the whole
  //     page and its own overflow-x container never engaged.
  //   • w-0 min-w-full — pins the content div to exactly the viewport's
  //     width in every sizing context, so tracks always resolve against a
  //     definite width and wide children scroll inside their own containers.
  return (
    <ScrollArea
      data-content-scroll
      className="min-h-0 h-full"
      contentClassName="w-0 min-w-full grid grid-cols-[minmax(0,1fr)] gap-[12px] pb-[18px]"
      options={SCROLL_OPTIONS}
    >
      <PageHero
        eyebrow={hero.eyebrow || (terminal ? DIMENSIONS[terminal.dim]?.label : "Tenant")}
        title={hero.title}
        detail={hero.detail}
        metrics={hero.metrics}
      />
      <div aria-busy={creds === undefined ? "true" : undefined}>
        <SegmentedTabs tabs={tabs} active={view} onSelect={setView} />
      </div>
      {view === "register" ? (
        <RegisterAnatomy scopePath={scopePath} />
      ) : view === "discover" && llmReady ? (
        <AnatomyDiscoverPanel
          run={discoverRun}
          onRun={runDiscover}
          onAbort={() => discoverAbortRef.current?.abort()}
          scopeKey={scopeKey}
          scopeLabel={scopeLabel}
          onNavigateTarget={onNavigateTarget}
          onOpenTrace={ctx?.traceLog?.onSelectTrace
            ? () => ctx.traceLog.onSelectTrace(discoverTraceId(scopeKey))
            : null}
          modelControl={modelControl}
        />
      ) : (
        <>
          {/* Where you are — committed levels (or the Global origin at the root) +
              the auto-drilled highest-volume descent. */}
          <ScopePositionStack
            scopePath={scopePath}
            range={range}
            onNavigatePath={onNavigatePath}
            descent={descent}
            descentLoading={descentLoading}
          />
          {/* Who / what drives it — columns chosen by distinctiveness. */}
          <ScopeComposition
            scopePath={scopePath}
            range={range}
            onNavigatePath={onNavigatePath}
            preferDims={preferDims}
          />
        </>
      )}
    </ScrollArea>
  );
}
